/*
These do not work currently because the graphql-java counter side does not support yet :

@include(if: Boolean) Only include this field in the result if the argument is true.
@skip(if: Boolean) Skip this field if the argument is true.
or for groups of fields
 ... @include(if: $expandedInfo) {
   firstName
   lastName
   birthday
 }

 See // http://graphql.org/learn/queries/#fragments
*/

/*
 Regarding the query tables (end of this file) we should be able to switch fields within the query
 depending on the returned object type :
 ... on Gene {
    ...GeneGrid
 }
 ... on Protein {
    ...ProteinGrid
 }
 but for this to work the GeneTableData-like objects need to deal with interfaces
*/
import { gql } from "@apollo/client";

import {
  WorkflowExtendedParameterFragmentDoc,
  WorkflowParameterFragmentDoc,
} from "@/modules/workflow/graphql/workflow.gql.ts";

import type { Dictionary } from "../app-utils/type.js";
import { COLUMN_DYNAMIC_TYPES, ENTITY_TYPE } from "../settings/settings.js";
import type { IDataTableQueryParams } from "./WebApiClient.js";

export const Fragments: any = {};
export const GridFragments: any = {};
export const DynamicFragments: Dictionary<string> = {};

Fragments.userBasics = gql`
  fragment UserBasics on User {
    id
    username
    firstName
    lastName
    email
    display
  }
`;

Fragments.userParams = gql`
  fragment UserParams on UserParams {
    projectItemSortOrder
    homeItemSortOrder
    lastSaveTarget
    firstLogin
    showTips
    lastAdvancedSearchType
    lastAdvancedSearchSubTypeId
    wrapColumnHeaders
    showFlagsTrack
    useSpecificFlagsColoring
    condensedFlagsDisplay
    showCommunityAnnotations
    referenceGenome
    defaultTracks {
      name
      type
      subTypeId
      pinned
      trackCustomization {
        configuredPhenotypes {
          id
          idType
          minValue
          maxValue
          logScale
          colorScheme {
            colors
          }
        }
      }
    }
  }
`;

Fragments.userGroupBasics = gql`
  fragment UserGroupBasics on UserGroup {
    id
    name
    description
  }
`;

Fragments.stringMapEntry = gql`
  fragment StringMapEntry on StringMapEntry {
    key
    label
    value
    multiValueIndex
  }
`;

Fragments.capability = gql`
  fragment Capability on Capability {
    id
    name
    groupName
    description
    type
  }
`;

Fragments.userGroupWithMembers = gql`
  fragment UserGroupWithMembers on UserGroup {
    ...UserGroupBasics
    members {
      id
      firstName
      lastName
      username
    }
  }
  ${Fragments.userGroupBasics}
`;

/* projects { id name  } is needed in UserGroupAdminPage.deleteUserGroupDisabledTooltip */
// user groups with member ids are needed in the User Groups admin page, wto match user ids from the table against the user groups
Fragments.userGroup = gql`
  fragment UserGroup on UserGroup {
    ...UserGroupBasics
    members {
      id
    }
    organisms {
      id
    }
    sampleTypes {
      id
    }
    projects {
      id
      name
    }
  }
  ${Fragments.userGroupBasics}
`;

Fragments.projectMember = gql`
  fragment ProjectMember on ProjectMember {
    roleId
    user {
      ...UserBasics
    }
  }
  ${Fragments.userBasics}
`;

// userGroups are needed with members for e.g. the project creation dialog, where you can add specific users depending on the user group you choose
Fragments.currentUser = gql`
  fragment CurrentUser on User {
    ...UserBasics
    capabilities {
      ...Capability
    }
    params {
      ...UserParams
    }
    userGroups {
      ...UserGroupWithMembers
    }
    roles {
      id
      name
      description
    }
  }
  ${Fragments.userBasics}
  ${Fragments.capability}
  ${Fragments.userParams}
  ${Fragments.userGroupWithMembers}
`;

Fragments.roleBasics = gql`
  fragment RoleBasics on Role {
    id
    name
  }
`;

Fragments.roleAdmin = gql`
  fragment RoleAdmin on Role {
    ...RoleBasics
    description
    users {
      id
    }
    capabilities {
      id
      type
    }
    legacy
  }
  ${Fragments.roleBasics}
`;

Fragments.regionTile = gql`
  fragment RegionTile on Region {
    contig {
      id
      sequenceId
      organism {
        id
        name
        species {
          id
          name
        }
      }
    }
    range {
      start
      stop
    }
  }
`;

Fragments.organismTile = gql`
  fragment OrganismTile on Organism {
    id
    name
    type
    description
    hidden
    species {
      name
    }
  }
`;

Fragments.projectFileTile = gql`
  fragment ProjectFileTile on ProjectFile {
    id
    fileName
    url
    humanReadablePath
    fullPath
    description
    created
    lastModified
    deleted
    hidden
    indexingStatus
    previewable
    creator {
      ...UserBasics
    }
    lastModifier {
      ...UserBasics
    }
    project {
      id
      name
      color
    }
    typeDisplayName
    contentType {
      name
      suffix
      description
      id
    }
  }
  ${Fragments.userBasics}
`;

Fragments.genomeViewerFile = gql`
  fragment GenomeViewerFile on ProjectFile {
    id
    fileName
    humanReadablePath
    path
    deleted
    hidden
  }
`;

Fragments.dynamicTerm = gql`
  fragment DynamicTerm on DynamicVocabularyTerm {
    itmId
    name
    type
  }
`;

Fragments.playbookConfig = gql`
  fragment PlaybookConfig on PlaybookConfig {
    icon
    sampleStatus {
      afterSubmission
      whileRunning
      afterCompletion
      failure
    }
    experimentInput {
      trackerAttributeId
      workflowParamId
      workflowParamDefault
      description
      label
      correctedTypeId
      correctedMandatory
    }
    sampleInput {
      trackerAttributeId
      workflowParamId
      workflowParamDefault
      description
      label
      correctedTypeId
      correctedMandatory
    }
    ngsWorkflow {
      workflowId
      workflowExportMappings {
        exportedWorkflowFieldName
        sampleAttributeId
      }
      workflowImportMappings {
        workflowFieldId
        attributeId
      }
    }
  }
`;

Fragments.playbookBasics = gql`
  fragment PlaybookBasics on Playbook {
    id
    identifier
    name
    nameSuffix
    description
    modified
    enabled
    order
    protectedPlaybook
    standard
    config {
      ...PlaybookConfig
    }
    nfConfig {
      icon
    }
    p2Config {
      icon
      contexts {
        __typename
        ... on GenomeViewerUiContext {
          context
          genomeParam {
            instanceId
            parameterId
          }
          regionParam {
            instanceId
            parameterId
          }
        }
        ... on PlaybookPageUiContext {
          context
        }
      }
    }
    type {
      id
      name
      class
    }
    categories {
      id
      name
      imageName
    }
    helpLink
    tooltip
    sampleType {
      id
    }
    uiContexts
  }
  ${Fragments.playbookConfig}
`;

Fragments.workflowExtendedConfig = gql`
  fragment WorkflowExtendedConfig on WorkflowExtendedConfig {
    activities {
      identifier
      name
      label
      status
      version
      disabled
      description
      parameters {
        ...WorkflowExtendedParameter
      }
    }
  }
  ${WorkflowExtendedParameterFragmentDoc}
`;

Fragments.p2PlaybookConfig = gql`
  fragment P2PlaybookConfig on P2PlaybookConfig {
    icon
    contexts {
      __typename
      ... on GenomeViewerUiContext {
        context
        genomeParam {
          instanceId
          parameterId
        }
        regionParam {
          instanceId
          parameterId
        }
      }
      ... on PlaybookPageUiContext {
        context
      }
    }
    workflow {
      id
      extendedConfig {
        ...WorkflowExtendedConfig
      }
    }
  }
  ${Fragments.workflowExtendedConfig}
`;

Fragments.playbookEventTile = gql`
  fragment PlaybookEventTile on PlaybookEvent {
    id
    alias
    note
    status
    created
    playbook {
      id
      name
      nameSuffix
      protectedPlaybook
      standard
      type {
        id
        name
        class
      }
      config {
        icon
      }
    }
    owner {
      ...UserBasics
    }
    project {
      id
      name
      description
      color
    }
    job {
      tool
    }
    shortSummary
    results {
      resourceType
      file {
        fileName
      }
    }
  }
  ${Fragments.userBasics}
`;

Fragments.playbookCategoryBasics = gql`
  fragment PlaybookCategoryBasics on PlaybookCategory {
    id
    name
    protectedCategory
    imageName
    playbooks {
      id
      name
    }
  }
`;

Fragments.organismBasics = gql`
  fragment OrganismBasics on Organism {
    id
    name
    hidden
    identifier
    description
    groupType
    strain
    genotypeDescription
    species {
      id
      name
    }
  }
`;

Fragments.organismForSearch = gql`
  fragment OrganismForSearch on Organism {
    id
    name
    species {
      id
      name
    }
  }
`;

Fragments.eventResource = gql`
  fragment EventResource on EventResource {
    activity
    direction
    file {
      ...ProjectFileTile
    }
    folder {
      path
      humanReadablePath
      deleted
      hidden
    }
    resourceType
    region {
      ...RegionTile
    }
    organism {
      ...OrganismTile
    }
  }
  ${Fragments.userBasics}
  ${Fragments.projectFileTile}
  ${Fragments.organismTile}
  ${Fragments.regionTile}
`;

Fragments.sampleTile = gql`
  fragment SampleTile on Sample {
    id
    name
    description
    locked
    status {
      name
      color
    }
    type {
      name
    }
    created
    modified
    project {
      id
      name
      color
    }
    deleted
    hidden
  }
`;

Fragments.sampleParentTile = gql`
  fragment SampleParentTile on Parent {
    id
    name
    description
    sampleCount
    parentType {
      name
    }
  }
`;

Fragments.eventSummary = gql`
  fragment EventSummary on PlaybookEvent {
    id
    alias
    note
    status
    created
    groupType
    workflowName
    playbook {
      id
      created
      modified
      workflowApprovalDate
      description
      name
      nameSuffix
      protectedPlaybook
      standard
      categories {
        name
      }
      type {
        id
        name
        class
      }
    }
    owner {
      ...UserBasics
    }
    project {
      id
      name
      description
      color
    }
    job {
      id
      name
      created
      errorDescription
      status
    }
  }
  ${Fragments.userBasics}
`;

Fragments.userForAdmin = gql`
  fragment UserForAdmin on User {
    ...UserBasics
    isActive
    lastUse
    accountStatus
    capabilities {
      ...Capability
    }
    userGroups {
      ...UserGroupBasics
    }
    roles {
      ...RoleBasics
    }
  }
  ${Fragments.userGroupBasics}
  ${Fragments.userBasics}
  ${Fragments.roleBasics}
  ${Fragments.capability}
`;

Fragments.projectActivityGroup = gql`
  fragment ProjectActivityGroup on Group {
    id
    name
    description
    type
    typeDisplayName
    itemCount
  }
`;

Fragments.projectBasics = gql`
  fragment ProjectBasics on Project {
    id
    guid
    name
    description
    isPublic
    isUserAdmin
    isPrivateWorkspace
    lastModified
    color
    capabilities
    created
    userGroup {
      id
      guid
      name
      description
    }
  }
`;

Fragments.projectMeta = gql`
  fragment ProjectMeta on Project {
    status
    statusNotes
    endNotes
    goal
    startDate
    lastUpdate
    initialEndDate
    actualEndDate
    projectedEndDate
  }
`;

Fragments.projectSequenceOrganisms = gql`
  fragment ProjectSequencedOrganisms on Project {
    userGroup {
      sequencedOrganisms {
        id
        name
      }
    }
  }
`;

Fragments.savedItemBasics = gql`
  fragment SavedItemBasics on SavedItem {
    id
    type
    name
    description
    owner
    project {
      id
      name
      color
    }
  }
`;

Fragments.savedItem = gql`
  fragment SavedItem on SavedItem {
    ...SavedItemBasics
    shared
    deletable
    created
    modified
    typeDisplayName
    project {
      isPrivateWorkspace
      isPublic
      isUserAdmin
    }
    creator {
      ...UserBasics
    }
    itemTags {
      id
      name
      color
    }
  }
  ${Fragments.savedItemBasics}
  ${Fragments.userBasics}
`;

Fragments.savedItemForAttachment = gql`
  fragment SavedItemForAttachment on SavedItem {
    id
    type
    name
    shared
    description
    created
    owner
    typeDisplayName
    ... on Group {
      itemCount
    }
    ... on View {
      viewType
      viewLabel
    }
  }
`;

Fragments.groupBasics = gql`
  fragment GroupBasics on Group {
    ...SavedItem
    itemCount
    itemSubTypeId
  }
  ${Fragments.savedItem}
`;

// little sister of gridColumn / Column
// Caution: needs the attribute type info, otherwise createDynamicFragments will fail e.g. for dynamic terms in the editing grid
Fragments.tableColumn = gql`
  fragment TableColumn on Column {
    columnId
    name
    field
    cellType
    groupName
    groupField
    dynamic
    dynamicType
    fixed
    attribute {
      attributeType {
        id
      }
    }
  }
`;

Fragments.columnTemplate = gql`
  fragment Template on Template {
    id
    name
    columns {
      field
      columnId
      namespace
    }
    selected
    default
    active
  }
`;

Fragments.nuProfileType = gql`
  fragment NuProfileType on NuProfileType {
    id
    name
    description
  }
`;

Fragments.nuProfileStats = gql`
  fragment NuProfileStats on NuProfileStats {
    count
    profileTypeId
    profileTypeName
  }
`;

Fragments.nuFeatureType = gql`
  fragment NuFeatureType on NuFeatureType {
    id
    name
    color
  }
`;

Fragments.nuFeatureStats = gql`
  fragment NuFeatureStats on NuFeatureStats {
    count
    featureTypeId
    featureTypeName
    featureTypeColor
  }
`;

Fragments.aaFeatureType = gql`
  fragment AaFeatureType on AaFeatureType {
    id
    name
    color
  }
`;

Fragments.databaseBasics = gql`
  fragment DatabaseBasics on Database {
    id
    name
    mnemonic
    version
    owner
    isShared
    created
    modified
    isUploadTarget
    organisation {
      id
      name
    }
  }
`;

Fragments.aaFeatureStats = gql`
  fragment AaFeatureStats on AaFeatureStats {
    associatedProteins
    count
    featureTypeId
    featureTypeName
    featureTypeColor
  }
`;

Fragments.markerType = gql`
  fragment MarkerType on MarkerType {
    id
    name
    description
  }
`;

Fragments.markerStats = gql`
  fragment MarkerStats on MarkerStats {
    markerTypeId
    markerTypeName
    count
  }
`;

Fragments.organismPropertyTypeBasics = gql`
  fragment OrganismPropertyTypeBasics on OrganismPropertyType {
    id
    name
    group {
      opgId
      name
    }
  }
`;

Fragments.organismProperties = gql`
  fragment OrganismProperty on OrganismProperty {
    description
    id
    numValue
    value
    color
    organismPropertyType {
      ...OrganismPropertyTypeBasics
      description
      group {
        description
      }
      isDiscrete
      valueType
    }
  }
  ${Fragments.organismPropertyTypeBasics}
`;

Fragments.classificationBasics = gql`
  fragment ClassificationBasics on Classification {
    id
    name
    description
    displayName
    groupName
    schemeName
  }
`;

Fragments.ecNumber = gql`
  fragment EcNumber on EcNumber {
    id
    name
    description
  }
`;

Fragments.range = gql`
  fragment Range on Range {
    start
    stop
  }
`;

Fragments.contigBasics = gql`
  fragment ContigBasics on Contig {
    id
    name
    accession
    sequenceId
    version
    length
    groupType
    database {
      ...DatabaseBasics
    }
    organism {
      id
      name
      description
    }
  }
  ${Fragments.databaseBasics}
`;

Fragments.variantCounts = gql`
  fragment VariantCounts on VariantCounts {
    point
    insertion
    deletion
    indel
    translocationInsertion
    translocationDeletion
  }
`;

Fragments.contigStatistics = gql`
  fragment ContigStatistics on ContigStatistics {
    contigSize
    geneCount
    proteinsCount
    transcriptsCount
    markersCount {
      ...MarkerStats
    }
    nuFeaturesCount {
      ...NuFeatureStats
    }
    aaFeaturesCount {
      ...AaFeatureStats
    }
    nuProfilesCount {
      ...NuProfileStats
    }
    variantCounts {
      ...VariantCounts
    }
  }
  ${Fragments.markerStats}
  ${Fragments.nuFeatureStats}
  ${Fragments.aaFeatureStats}
  ${Fragments.nuProfileStats}
  ${Fragments.variantCounts}
`;

Fragments.genomeViewerContig = gql`
  fragment GenomeViewerContig on Contig {
    id
    name
    accession
    sequenceId
    sequenceType
    version
    length
    groupType
    organism {
      id
      name
    }
    contigStatistics {
      ...ContigStatistics
    }
    userGroups {
      ...UserGroupBasics
    }
  }
  ${Fragments.contigStatistics}
  ${Fragments.userGroupBasics}
`;

Fragments.regionBasics = gql`
  fragment RegionBasics on Region {
    contig {
      ...ContigBasics
    }
    range {
      ...Range
    }
    strand
  }
  ${Fragments.range}
  ${Fragments.contigBasics}
`;

Fragments.deletedGroup = gql`
  fragment DeletedGroup on DeletedGroup {
    id
    name
    type
    typeDisplayName
    itemCount
  }
`;

Fragments.parentSavedItem = gql`
  fragment ParentSavedItem on Parent {
    id
    name
    type
    typeDisplayName
    shared
    sampleCount
    status {
      name
      color
    }
    samples {
      organism {
        id
      }
    }
  }
`;

// TODO: delete (Used in Saved Items)
Fragments.regionForFlags = gql`
  fragment RegionForFlags on Region {
    contig {
      ...ContigBasics
      organism {
        id
        name
        species {
          id
          name
        }
      }
    }
    range {
      ...Range
    }
    strand
  }
  ${Fragments.range}
  ${Fragments.contigBasics}
`;

Fragments.projectActivityRegionFlag = gql`
  fragment ProjectActivityRegionFlag on RegionFlag {
    id
    name
    deleted
    type
    typeDisplayName
    region {
      contig {
        id
        accession
        sequenceId
        organism {
          id
          name
          species {
            id
            name
          }
        }
      }
      range {
        ...Range
      }
    }
  }
  ${Fragments.range}
`;

// TODO: delete (Used in Saved Items)
Fragments.regionFlag = gql`
  fragment RegionFlag on RegionFlag {
    ...SavedItem
    modified
    lastModifier {
      ...UserBasics
    }
    deleted
    region {
      ...RegionForFlags
    }
  }
  ${Fragments.savedItem}
  ${Fragments.userBasics}
  ${Fragments.regionForFlags}
`;

Fragments.coordinateList = gql`
  fragment CoordinateList on CoordinateList {
    ...SavedItem
    organism {
      ...OrganismBasics
    }
    count
    deleted
  }
  ${Fragments.savedItem}
  ${Fragments.organismBasics}
`;

Fragments.savedItemListing = gql`
  fragment SavedItemsListing on SavedItemsListing {
    count
    name
    subType
    subTypeId
    type
  }
`;

Fragments.track = gql`
  fragment Track on Track {
    name
    type
    subTypeId
    pinned
    trackCustomization {
      configuredPhenotypes {
        id
        idType
        minValue
        maxValue
        logScale
        colorScheme {
          colors
        }
        configuredTerms {
          term
          color
        }
      }
      expressionColoring {
        datasetId
        conditions
        minValue
        maxValue
        logScale
        colorScheme {
          colors
        }
      }
      bedConfiguration {
        orderedBedFiles {
          bedFileId
          color
        }
      }
      coordinateConfiguration {
        orderedCoordinateLists {
          coordinateListId
          color
        }
      }
      ngsConfiguration {
        coverageConfig {
          logScale
          yAxisScaling
          yAxisScalingMax
          yAxisScalingMin
        }
        orderedBamFiles {
          bamFileId
          visiblePileup
          coverageColor
          pileupDensity
          pileupLanesCount
          pileupColoring
          pileupIncludeSoftClippedReads
        }
      }
    }
  }
`;

Fragments.projectFile = gql`
  fragment ProjectFile on ProjectFile {
    id
    deletable
    created
    creator {
      ...UserBasics
    }
    lastModifier {
      ...UserBasics
    }
    project {
      id
      name
      color
      isPrivateWorkspace
      isPublic
      isUserAdmin
    }
    fileName
    fileType
    description
    lastModified
    deleted
    hidden
    typeDisplayName
    fileBrowseType
    isEditable
    isProtected
    indexingStatus
    contentType {
      name
      description
      id
    }
    url
  }
  ${Fragments.userBasics}
`;

Fragments.projectFileForBrowsing = gql`
  fragment ProjectFileForBrowsing on ProjectFile {
    ...ProjectFile
    fullPath
    humanReadablePath
    fileBrowseType
    canChangeProtectedStatus
    eventResources {
      playbookEvent {
        ...PlaybookEventTile
      }
    }
    size
    lastModified
    organisms {
      id
      name
      species {
        name
      }
    }
    parents {
      id
      name
      parentType {
        name
      }
    }
    samples {
      ...SampleTile
    }
  }
  ${Fragments.userBasics}
  ${Fragments.projectFile}
  ${Fragments.sampleTile}
  ${Fragments.playbookEventTile}
`;

Fragments.sampleReport = gql`
  fragment SampleReport on ProjectFile {
    ...ProjectFile
    parents {
      id
      name
    }
  }
  ${Fragments.projectFile}
`;

Fragments.projectDataset = gql`
  fragment ProjectDataset on ProjectFile {
    ...ProjectFile
    organisms {
      id
      name
    }
  }
  ${Fragments.projectFile}
`;

Fragments.analystSession = gql`
  fragment AnalystSession on AnalystSession {
    ...SavedItem
    modified
  }
  ${Fragments.savedItem}
`;

Fragments.projectActivityView = gql`
  fragment ProjectActivityView on View {
    id
    name
    description
    deleted
    type
    typeDisplayName
    viewType
    viewLabel
    params
  }
`;

Fragments.viewBasics = gql`
  fragment ViewBasics on View {
    ...SavedItem
    deleted
    version
    viewType
    viewLabel
    params
  }
  ${Fragments.savedItem}
`;

Fragments.projectActivity = gql`
  fragment ProjectActivity on ProjectActivity {
    id
    type
    isPrivate
    created
    commentBody
    deletable
    projectName
    affectedUsers {
      ...UserBasics
    }
    user {
      ...UserBasics
    }
    groups {
      ...ProjectActivityGroup
    }
    views {
      ...ProjectActivityView
    }
    deletedGroups {
      ...DeletedGroup
    }
    parents {
      id
      name
    }
    project {
      id
      color
      name
      isPublic
      userGroup {
        id
        name
      }
    }
    flags {
      ...ProjectActivityRegionFlag
    }
    coordinateLists {
      id
      deleted
      name
      typeDisplayName
      organism {
        id
        name
      }
    }
    playbookEvent {
      playbook {
        name
        type {
          id
        }
      }
      job {
        name
      }
      id
      alias
    }
    projectFiles {
      id
      fileName
      deleted
      url
      contentType {
        id
      }
      typeDisplayName
    }
  }
  ${Fragments.userBasics}
  ${Fragments.projectActivityGroup}
  ${Fragments.deletedGroup}
  ${Fragments.projectActivityView}
  ${Fragments.projectActivityRegionFlag}
`;

Fragments.editingAttributeValue = gql`
  fragment EditingAttributeValue on AttributeValue {
    id
    booleanValue
    longValue
    floatValue
    stringValue
    stringListValue
    dateValue
    dynamicTerm {
      ...DynamicTerm
    }
    projectFile {
      ...ProjectFileTile
    }
    attribute {
      id
    }
  }
  ${Fragments.projectFileTile}
  ${Fragments.dynamicTerm}
`;

Fragments.attributeValue = gql`
  fragment AttributeValue on AttributeValue {
    id
    booleanValue
    longValue
    floatValue
    stringValue
    stringListValue
    dateValue
    dynamicTerm {
      ...DynamicTerm
    }
    projectFile {
      ...ProjectFileTile
    }
    valueIndex
    color
    attribute {
      id
      attributeName
      groupName
      usage
      multipleValues
      isHead
      attributeType {
        id
        name
      }
    }
    applicable
  }
  ${Fragments.projectFileTile}
  ${Fragments.dynamicTerm}
`;

Fragments.parentBasics = gql`
  fragment ParentBasics on Parent {
    id
    name
    status {
      id
      name
      color
    }
    parentType {
      id
      name
    }
    project {
      id
      name
      color
    }
  }
`;

Fragments.workflowNotification = gql`
  fragment WorkflowNotification on WorkflowNotification {
    id
    parent {
      ...ParentBasics
    }
    status {
      id
      name
    }
    subtype
  }
  ${Fragments.parentBasics}
`;

// project is needed with member for the project administration (adding new user)
Fragments.project = gql`
  fragment Project on Project {
    ...ProjectBasics
    parentFileId
    ...ProjectMeta
    projectMembers {
      ...ProjectMember
    }
    userGroup {
      ...UserGroupWithMembers
    }
    creator {
      ...UserBasics
    }
    projectStatistics {
      eventCount
    }
  }
  ${Fragments.projectBasics}
  ${Fragments.projectMember}
  ${Fragments.projectMeta}
  ${Fragments.userBasics}
  ${Fragments.userGroupWithMembers}
`;

Fragments.contigList = gql`
  fragment ContigList on Contig {
    id
    name
    sequenceId
    accession
    description
    sequenceType
    organism {
      id
      name
    }
    database {
      id
      name
    }
    version
    length
    entered
    species {
      id
      name
    }
  }
`;

Fragments.probeList = gql`
  fragment ProbeList on Probe {
    id
    name
    sequenceId
    accession
    description
    sequenceType
    database {
      id
      name
    }
    creator {
      username
    }
    version
    length
    entered
  }
`;

Fragments.otherDnaList = gql`
  fragment OtherDnaList on OtherDna {
    id
    name
    sequenceId
    accession
    description
    sequenceType
    database {
      id
      name
    }
    creator {
      username
    }
    version
    length
    entered
  }
`;

Fragments.variantAnalysisGroupBasics = gql`
  fragment VariantAnalysisGroupBasics on VariantAnalysisGroup {
    id
    name
  }
`;

Fragments.variantAnalysisGroup = gql`
  fragment VariantAnalysisGroup on VariantAnalysisGroup {
    ...VariantAnalysisGroupBasics
    totalGenomeCount
    refGenomes {
      id
      name
      isReference
      species {
        id
        name
      }
    }
  }
  ${Fragments.variantAnalysisGroupBasics}
`;

Fragments.species = gql`
  fragment Species on Species {
    id
    name
    lineage
    synonym
    commonName
  }
`;

Fragments.markerStatus = gql`
  fragment MarkerStatus on MarkerStatus {
    id
    name
  }
`;

Fragments.genomeBrowserStatistics = gql`
  fragment GenomeBrowserStatistics on GenomeBrowserStatistics {
    geneCount
    proteinsCount
    transcriptsCount
    variantsCount
    markersCount {
      ...MarkerStats
    }
    nuFeaturesCount {
      ...NuFeatureStats
    }
    aaFeaturesCount {
      ...AaFeatureStats
    }
    nuProfilesCount {
      ...NuProfileStats
    }
    hasGenomeAnalysis
    hasVcfGenomeAnalysis
  }
  ${Fragments.markerStats}
  ${Fragments.nuFeatureStats}
  ${Fragments.aaFeatureStats}
  ${Fragments.nuProfileStats}
`;

Fragments.genomeStatistics = gql`
  fragment GenomeStatistics on GenomeStatistics {
    contigCount
    ecProteinAssignmentsCount
    ecProteinAssignmentsDistinctCount
    ecCoverage
    geneCount
    genesLinkedProteinsCount
    genesLinkedTranscriptsCount
    genomeSize
    orfsCount
    proteinsCount
    transcriptsCount
    flagCount
    markersCount {
      ...MarkerStats
    }
    nuFeaturesCount {
      ...NuFeatureStats
    }
    aaFeaturesCount {
      ...AaFeatureStats
    }
    nuProfilesCount {
      ...NuProfileStats
    }
  }
  ${Fragments.markerStats}
  ${Fragments.nuFeatureStats}
  ${Fragments.aaFeatureStats}
  ${Fragments.nuProfileStats}
`;

Fragments.relatedProject = gql`
  fragment RelatedProject on RelatedProject {
    project {
      ...ProjectBasics
    }
    activities {
      ...ProjectActivity
    }
  }
  ${Fragments.projectBasics}
  ${Fragments.projectActivity}
`;

/**
 *  variantAnalysisGroup: non-basic version is needed, we want the list of genomes to get the reference genome
 */
Fragments.genomeForGenomeBrowser = gql`
  fragment GenomeViewGenome on Organism {
    ...OrganismBasics
    isReference
    isVirtual
    variantAnalysisGroups {
      ...VariantAnalysisGroup
    }
  }
  ${Fragments.organismBasics}
  ${Fragments.variantAnalysisGroup}
`;

Fragments.externalReference = gql`
  fragment ExternalReference on ExternalReference {
    id
    name
    description
    databaseName
    displayName
    link
  }
`;
/**
 * Use this fragment only on summary pages that get additional fields for articles
 */
Fragments.literatureReference = gql`
  fragment LiteratureReference on ExternalReference {
    ...ExternalReference
    citation
    title
  }
  ${Fragments.externalReference}
`;

Fragments.gridOrganismProperty = gql`
  fragment GridOrganismProperty on OrganismProperty {
    id
    value
    numValue
    color
  }
`;

Fragments.datasetBasics = gql`
  fragment DatasetBasics on Dataset {
    id
    name
    description
    isGenomeDefault
    isGlobalDefault
    fileMetadata {
      ...ProjectFileForBrowsing
    }
  }
  ${Fragments.projectFileForBrowsing}
`;

Fragments.genomeSummaryNavbarInfo = gql`
  fragment GenomeSummaryNavbarInfo on Organism {
    id
    sequenced
    species {
      id
      name
    }
  }
`;

Fragments.genomeInfoCard = gql`
  fragment GenomeInfoCard on Organism {
    id
    name
    hidden
    identifier
    description
    strain
    created
    modified
    version
    taxonomy
    sequencingType
    genotypeDescription
    type
    species {
      ...Species
    }
    organisation {
      id
      name
    }
    variantAnalysisGroups {
      ...VariantAnalysisGroup
    }
    databases {
      ...DatabaseBasics
    }
    userGroups {
      ...UserGroupBasics
    }
    origins
  }
  ${Fragments.species}
  ${Fragments.variantAnalysisGroup}
  ${Fragments.databaseBasics}
  ${Fragments.userGroupBasics}
`;

Fragments.eventResourceForSummary = gql`
  fragment EventResourceForSummary on EventResource {
    direction
    playbookEvent {
      ...PlaybookEventTile
    }
  }
  ${Fragments.playbookEventTile}
`;

Fragments.genomeSummary = gql`
  fragment GenomeSummary on Organism {
    id
    groupType
    ...GenomeSummaryNavbarInfo
    ...GenomeInfoCard
    customId
    isReference
    hasVariants
    hasGenomeAnalysis
    hasVcfGenomeAnalysis
    hasContigs
    hasGenes
    hasTranscripts
    hasProteins
    hasVariants
    hasFlags
    hasGenomeAnalysis
    hasVcfGenomeAnalysis
    hasContigs
    hasGenes
    hasMarkers
    hasNuFeatures
    allExternalReferences {
      ...ExternalReference
    }
    literatureReferences {
      ...LiteratureReference
    }
    datasets {
      ...DatasetBasics
    }
    organismProperties {
      ...OrganismProperty
    }
    sample {
      id
      hidden
      primaryParent {
        ...SampleParentTile
        status {
          name
          color
        }
        sampleCount
      }
    }
    variantAnalysisGroups {
      ...VariantAnalysisGroup
    }
  }
  ${Fragments.genomeSummaryNavbarInfo}
  ${Fragments.genomeInfoCard}
  ${Fragments.externalReference}
  ${Fragments.literatureReference}
  ${Fragments.datasetBasics}
  ${Fragments.organismProperties}
  ${Fragments.sampleParentTile}
  ${Fragments.variantAnalysisGroup}
`;

Fragments.genomeRelatedProjects = gql`
  fragment GenomeRelatedProjects on Organism {
    id
    relatedProjects {
      ...RelatedProject
    }
  }
  ${Fragments.relatedProject}
`;

Fragments.proteinBasics = gql`
  fragment ProteinBasics on Protein {
    id
    systematicName
    sequenceId
    accession
    alternativeName
    length
    description
    note
    proteinNames
    groupType
    species {
      name
    }
    genome {
      ...OrganismBasics
      isReference
      userGroups {
        ...UserGroupBasics
      }
      sequencingType
    }
    database {
      ...DatabaseBasics
    }
  }
  ${Fragments.organismBasics}
  ${Fragments.databaseBasics}
  ${Fragments.userGroupBasics}
`;

Fragments.transcriptLocation = gql`
  fragment TranscriptLocation on TranscriptLocation {
    id
    groupType
    region {
      ...RegionBasics
    }
    transcriptMappings {
      start
      stop
    }
  }
  ${Fragments.regionBasics}
`;

Fragments.region = gql`
  fragment Region on Region {
    contig {
      ...ContigBasics
    }
    ...RegionBasics
  }
  ${Fragments.contigBasics}
  ${Fragments.regionBasics}
`;

Fragments.geneBasics = gql`
  fragment GeneBasics on Gene {
    id
    name
    geneType
    description
    database {
      ...DatabaseBasics
    }
    synonyms
    length
    groupType
    region {
      ...RegionBasics
    }
  }
  ${Fragments.regionBasics}
  ${Fragments.databaseBasics}
`;

Fragments.transcriptsListInfoCard = gql`
  fragment TranscriptsListInfoCard on Transcript {
    id
    name
    sequenceId
    sequenceTypeId
    description
    genome {
      name
    }
    database {
      name
    }
  }
`;

Fragments.transcriptBasics = gql`
  fragment TranscriptBasics on Transcript {
    id
    name
    sequenceId
    accession
    length
    description
    groupType
    sequenceTypeId
    sequenceType
    species {
      name
    }
    genome {
      ...OrganismBasics
      userGroups {
        ...UserGroupBasics
      }
    }
    database {
      ...DatabaseBasics
    }
  }
  ${Fragments.databaseBasics}
  ${Fragments.organismBasics}
  ${Fragments.userGroupBasics}
`;

Fragments.geneSummary = gql`
  fragment GeneSummary on Gene {
    ...GeneBasics
    hasVariants
    synonyms
    genome {
      ...GenomeInfoCard
      components {
        ...OrganismTile
      }

      virtualGenomes {
        ...OrganismTile
        sequencingType
      }
    }
    proteins {
      ...ProteinBasics
    }
    transcripts {
      ...TranscriptsListInfoCard
    }
    relatedProjects {
      ...RelatedProject
    }
    allClassifications {
      ...ClassificationBasics
    }
    allExternalReferences {
      ...ExternalReference
    }
    literatureReferences {
      ...LiteratureReference
    }
  }
  ${Fragments.geneBasics}
  ${Fragments.genomeInfoCard}
  ${Fragments.organismTile}
  ${Fragments.proteinBasics}
  ${Fragments.transcriptsListInfoCard}
  ${Fragments.relatedProject}
  ${Fragments.classificationBasics}
  ${Fragments.externalReference}
  ${Fragments.literatureReference}
`;

Fragments.proteinLocation = gql`
  fragment ProteinLocation on ProteinLocation {
    id
    groupType
    region {
      ...RegionBasics
    }
    proteinLocationMappings {
      start
      stop
    }
  }
  ${Fragments.regionBasics}
`;

Fragments.internalReference = gql`
  fragment InternalReference on Sequence {
    id
    database {
      id
      mnemonic
    }
    sequenceId
    name
    identifier
    sequenceType
    sequenceGroup {
      name
    }
    accession
    version
    description
  }
`;

Fragments.pathwayNames = gql`
  fragment PathwayNames on Pathway {
    name
    description
  }
`;

Fragments.blastResult = gql`
  fragment BlastResult on BlastResult {
    id
    identicals
    positives
    queryStart
    queryStop
    targetStart
    targetStop
    score
    pValue
    eValue
    alignment
    protein {
      id
      accession
      sequenceId
      description
      length
      genome {
        id
        name
      }
    }
  }
`;

Fragments.proteinSummary = gql`
  fragment ProteinSummary on Protein {
    ...ProteinBasics
    genome {
      ...GenomeInfoCard
      components {
        ...OrganismTile
      }

      virtualGenomes {
        ...OrganismTile
        sequencingType
      }
    }
    hasVariants
    orthologTypes
    proteinNames
    ecNumbers {
      ...EcNumber
    }
    proteinLocations {
      ...ProteinLocation
    }
    relatedProjects {
      ...RelatedProject
    }
    genes {
      ...GeneBasics
    }
    transcripts {
      ...TranscriptsListInfoCard
    }
    allClassifications {
      ...ClassificationBasics
    }
    allExternalReferences {
      ...ExternalReference
    }
    literatureReferences {
      ...LiteratureReference
    }
    orthologTypes
    version
    length
    isoelectricPoint
    molecularWeight
    featureStatsData {
      count
      featureTypeId
      featureTypeName
    }
    internalReferences {
      ...InternalReference
    }
    pathways {
      ...PathwayNames
    }
    bestBlastResults {
      ...BlastResult
    }
    creator {
      ...UserBasics
    }
  }
  ${Fragments.proteinBasics}
  ${Fragments.genomeInfoCard}
  ${Fragments.organismTile}
  ${Fragments.userGroupBasics}
  ${Fragments.databaseBasics}
  ${Fragments.ecNumber}
  ${Fragments.proteinLocation}
  ${Fragments.relatedProject}
  ${Fragments.transcriptsListInfoCard}
  ${Fragments.geneBasics}
  ${Fragments.classificationBasics}
  ${Fragments.externalReference}
  ${Fragments.literatureReference}
  ${Fragments.internalReference}
  ${Fragments.pathwayNames}
  ${Fragments.blastResult}
  ${Fragments.userBasics}
`;

Fragments.transcriptSummary = gql`
  fragment TranscriptSummary on Transcript {
    ...TranscriptBasics
    genome {
      ...GenomeInfoCard
      components {
        ...OrganismTile
      }

      virtualGenomes {
        ...OrganismTile
        sequencingType
      }
    }
    transcriptLocations {
      ...TranscriptLocation
    }
    relatedProjects {
      ...RelatedProject
    }
    genes {
      ...GeneBasics
    }
    proteins {
      ...ProteinBasics
    }
    allClassifications {
      ...ClassificationBasics
    }
    allExternalReferences {
      ...ExternalReference
    }
    literatureReferences {
      ...LiteratureReference
    }
    version
    length
    internalReferences {
      ...InternalReference
    }
    creator {
      ...UserBasics
    }
  }
  ${Fragments.transcriptBasics}
  ${Fragments.genomeInfoCard}
  ${Fragments.organismTile}
  ${Fragments.transcriptLocation}
  ${Fragments.relatedProject}
  ${Fragments.proteinBasics}
  ${Fragments.geneBasics}
  ${Fragments.classificationBasics}
  ${Fragments.externalReference}
  ${Fragments.literatureReference}
  ${Fragments.internalReference}
  ${Fragments.userBasics}
  ${Fragments.userGroupBasics}
`;

Fragments.contigInfoCard = gql`
  fragment ContigInfoCard on Contig {
    id
    sequenceId
    sequenceType
    name
    description
    creator {
      ...UserBasics
    }
    type
    species {
      id
      name
    }
  }
  ${Fragments.userBasics}
`;

Fragments.contigSummary = gql`
  fragment ContigSummary on Contig {
    ...ContigBasics
    ...ContigInfoCard
    organism {
      ...GenomeInfoCard
      components {
        ...OrganismTile
      }

      virtualGenomes {
        ...OrganismTile
        sequencingType
      }
    }
    database {
      ...DatabaseBasics
    }
    relatedProjects {
      ...RelatedProject
    }
    contigStatistics {
      ...ContigStatistics
    }
    internalReferences {
      ...InternalReference
    }
    eventResources {
      ...EventResourceForSummary
    }
  }
  ${Fragments.contigBasics}
  ${Fragments.genomeInfoCard}
  ${Fragments.organismTile}
  ${Fragments.databaseBasics}
  ${Fragments.relatedProject}
  ${Fragments.contigStatistics}
  ${Fragments.internalReference}
  ${Fragments.contigInfoCard}
  ${Fragments.eventResourceForSummary}
`;

Fragments.sequenceSummary = gql`
  fragment SequenceSummary on Sequence {
    id
    sequenceGroup {
      name
    }
    sequenceType
    name
    accession
    sequenceId
    version
    length
    description
    creator {
      ...UserBasics
    }
    length
    proteins {
      ...ProteinBasics
    }
    transcripts {
      ...TranscriptsListInfoCard
    }
    database {
      ...DatabaseBasics
    }
    organism {
      ...OrganismBasics
    }
    relatedProjects {
      ...RelatedProject
    }
    allClassifications {
      ...ClassificationBasics
    }
    internalReferences {
      ...InternalReference
    }
  }
  ${Fragments.proteinBasics}
  ${Fragments.transcriptsListInfoCard}
  ${Fragments.organismBasics}
  ${Fragments.databaseBasics}
  ${Fragments.relatedProject}
  ${Fragments.classificationBasics}
  ${Fragments.internalReference}
  ${Fragments.userBasics}
`;

Fragments.feature = gql`
  fragment Feature on Feature {
    id
    targetId
    name
    synonyms
    description
    type
    featureTypeId
    sequenceTypeId
    referenced
    color
    mappings {
      ...Range
    }
    region {
      range {
        ...Range
      }
      strand
    }
    origRange {
      ...Range
    }
    protein {
      id
      accession
      sequenceId
    }
    score
    eValue
    extDatabaseName
    extReferences {
      displayName
      link
    }
    status
  }
  ${Fragments.range}
`;

Fragments.featureWithSequence = gql`
  fragment FeatureWithSequence on Feature {
    ...Feature
    sequence
  }
  ${Fragments.feature}
`;

Fragments.nuProfile = gql`
  fragment NuProfile on NuProfile {
    rangeStart
    rangeStop
    score
  }
`;

Fragments.profileFeatures = gql`
  fragment ProfileFeatures on TrackNuProfile {
    range {
      ...Range
    }
    overflow
    profiles {
      ...NuProfile
    }
  }
  ${Fragments.range}
  ${Fragments.nuProfile}
`;

Fragments.dataSeries = gql`
  fragment DataSeries on DataSeries {
    name
    rows {
      id
      values {
        key
        value
        multiValueIndex
      }
    }
  }
`;

Fragments.trackFeatures = (withSequence?: boolean) => {
  return gql`
    fragment TrackFeatures on TrackFeatures {
      featureType
      contig {
        ...ContigBasics
      }
      features {
        ${withSequence ? "...FeatureWithSequence" : "...Feature"}
      }
      overflow
    }
    ${Fragments.contigBasics}
    ${withSequence ? Fragments.featureWithSequence : Fragments.feature}
  `;
};

Fragments.trackExpressionValues = gql`
  fragment TrackExpressionValues on TrackFeatures {
    featureType
    features {
      id
      dataSeries {
        ...DataSeries
      }
    }
  }
  ${Fragments.dataSeries}
`;

Fragments.variantBasics = gql`
  fragment VariantBasics on Variant {
    id
    name
    variantType
    groupType
    isReference
    isPresent
    refAllele
    altAllele
    refAlleleLength
    altAlleleLength
    alleleLength
    profile {
      genomeId
      symbol
      zygosity
    }
    region {
      range {
        start
        stop
      }
    }
  }
`;

Fragments.vcfVariantBasics = gql`
  fragment VcfVariantBasics on VCFVariant {
    id
    name
    alleleLength
    variantType
    isReference
    refAllele
    altAllele
    refAlleleLength
    altAlleleLength
    profileVariant {
      genomeId
      genomeName
      present
      heterozygous
      noCall
    }
    region {
      range {
        start
        stop
      }
    }
  }
`;

Fragments.variant = gql`
  fragment Variant on Variant {
    id
    groupType
    name
    preferredName
    description
    variantType
    refAllele
    refAlleleLength
    altAllele
    altAlleleLength
    allele
    alleleLength
    isReference
    isPresent
    effect
    profile {
      genomeId
      symbol
    }
    coverage
    quality
    zygosity
    locationClass
    mutId
    genome {
      ...OrganismBasics
    }
    refGenome {
      ...OrganismBasics
    }
    region {
      ...Region
    }
    refRegion {
      ...Region
    }
    variantAnalysisGroup {
      ...VariantAnalysisGroup
    }
    refVariant {
      id
      name
      genome {
        id
        name
      }
    }
    refVariants {
      id
      name
      genome {
        id
        name
        species {
          name
        }
      }
      region {
        ...Region
      }
    }
    higherGenes {
      id
      name
    }
    lowerGenes {
      id
      name
    }
    higherVariants {
      id
      mutId
      name
    }
    lowerVariants {
      id
      mutId
      name
    }
  }
  ${Fragments.region}
  ${Fragments.organismBasics}
  ${Fragments.variantAnalysisGroup}
`;

Fragments.genMutation = gql`
  fragment GenMutation on GenMutation {
    id
    groupType
    hasAffectedGenes
    hasAffectedProteins
    hasRelatedMutations
    refVariant {
      ...Variant
      proteinMutations {
        id
        name
        range {
          start
          stop
        }
        proteinLocation {
          id
          protein {
            id
            accession
            sequenceId
            description
          }
        }
      }
      geneMutations {
        id
        locationClass
        effect
        gene {
          id
          name
        }
      }
    }
    mutVariants {
      genome {
        id
        name
        species {
          name
        }
      }
      region {
        ...Region
      }
    }
    relatedProjects {
      ...RelatedProject
    }
  }
  ${Fragments.variant}
  ${Fragments.region}
  ${Fragments.relatedProject}
`;

Fragments.trackVariants = gql`
  fragment TrackVariants on TrackVariants {
    contig {
      ...ContigBasics
    }
    range {
      ...Range
    }
    variants {
      ...VariantBasics
      mutId
      zygosity
    }
    variantCount
    overflow
    refGenomeId
  }
  ${Fragments.contigBasics}
  ${Fragments.range}
  ${Fragments.variantBasics}
`;

Fragments.trackVcfVariants = gql`
  fragment TrackVcfVariants on TrackVCFVariants {
    contig {
      ...ContigBasics
    }
    range {
      ...Range
    }
    variants {
      ...VcfVariantBasics
    }
    variantCount
    overflow
  }
  ${Fragments.contigBasics}
  ${Fragments.range}
  ${Fragments.vcfVariantBasics}
`;

Fragments.globalSearchSuggestions = gql`
  fragment GlobalSearchSuggestions on GlobalSearchSuggestions {
    searchTerm
    categories {
      totalItemCount
      categoryType
      categoryName
      subTypeId
      items {
        id
        name
        description
        type
        typeName
      }
    }
  }
`;

Fragments.searchResult = gql`
  fragment SearchResult on SearchResult {
    id
    name
    groups {
      id
      type
      name
      description
      owner
      typeDisplayName
      itemCount
      itemSubTypeId
      firstItemId
    }
    advancedSearchParams {
      searchType
      searchSubTypeId
      quickSearchQuery
      restrictionType
      restrictions {
        id
        name
        restrictionType
        secondaryName
      }
      attributesQueryExpression
    }
  }
`;

Fragments.searchAttribute = gql`
  fragment SearchAttribute on SearchAttribute {
    field
    name
    groupName
    description
    cellType
    values {
      key
      label
    }
    operators {
      key
      label
    }
    minValue
    maxValue
    isInteger
  }
`;

Fragments.sequence = gql`
  fragment Sequence on Sequence {
    id
    sequenceId
    name
    accession
    version
    description
    length
    sequenceGroup {
      name
    }
    organism {
      id
      name
      species {
        id
        name
      }
    }
    database {
      id
      name
      mnemonic
      version
    }
    species {
      id
      name
    }
  }
`;

Fragments.gridColumn = gql`
  fragment GridColumn on Column {
    name
    field
    cellType
    fixed
    dynamic
    dynamicType
    sort {
      direction
      priority
    }
    sortable
    filterable
    hidden
    columnId
    namespace
    groupName
    groupField
    filterExpression
    cellExpansion
    multiValuesContext
    searchAttributes {
      ...SearchAttribute
    }
    attribute {
      id
      decimals
      columnSet {
        id
      }
      representedColumn
      attributeType {
        id
      }
      automatic
      modifiable
      editable
      multipleValues
    }
  }
  ${Fragments.searchAttribute}
`;

GridFragments.geneGrid = (queryParams: IDataTableQueryParams) => {
  return gql`
    fragment GeneGrid on Gene {
      ...GeneBasics
      genome {
        ...OrganismBasics
        userGroups {
          ...UserGroupBasics
        }
      }
      ${
        queryParams.withProteins
          ? `
        proteins {
          id
          systematicName
          alternativeName
          sequenceId
        }
        `
          : ""
      }
      ${
        queryParams.withTranscripts
          ? `
        transcripts {
          id
          name
          sequenceId
        }
        `
          : ""
      }
      ${
        queryParams.withLiteratureReferences
          ? `
        literatureReferences {
          ...ExternalReference
        }
        `
          : ""
      }
      ${
        queryParams.withOtherReferences
          ? `
        externalReferences {
          ...ExternalReference
        }
        `
          : ""
      }
    }
    ${Fragments.geneBasics}
    ${Fragments.organismBasics}
    ${Fragments.userGroupBasics}
    ${queryParams.withLiteratureReferences || queryParams.withOtherReferences ? Fragments.externalReference : ""}
  `;
};

GridFragments.contigGrid = (queryParams: IDataTableQueryParams) => {
  return gql`
    fragment ContigGrid on Contig {
      ...ContigList
      database {
        mnemonic
      }
      creator {
        username
      }
      ${
        queryParams.withInternalReferences
          ? `
        internalReferences {
          ...InternalReference
        }
        `
          : ""
      }
    }
    ${Fragments.contigList}
    ${queryParams.withInternalReferences ? Fragments.internalReference : ""}
  `;
};

GridFragments.probeGrid = () => {
  return gql`
    fragment ProbeGrid on Probe {
      ...ProbeList
      database {
        mnemonic
      }
    }
    ${Fragments.probeList}
  `;
};

GridFragments.otherDnaGrid = () => {
  return gql`
    fragment OtherDnaGrid on OtherDna {
      ...OtherDnaList
      database {
        mnemonic
      }
    }
    ${Fragments.otherDnaList}
  `;
};

GridFragments.genericSeqGrid = (_queryParams: IDataTableQueryParams) => {
  return gql`
    fragment GenericSeqGrid on Sequence {
      id
      sequenceId
      description
      sequenceType
      sequenceGroup {
        name
        label
      }
      length
      creator {
        display
      }
      entered
      database {
        id
        mnemonic
        version
      }
      organism {
        id
        name
      }
    }
  `;
};

GridFragments.transcriptGrid = (queryParams: IDataTableQueryParams) => {
  return gql`
    fragment TranscriptGrid on Transcript {
      ...TranscriptBasics
      version
      length
      creator {
        username
      }
      groupType
      ${
        queryParams.withGenes
          ? `
        genes {
          ...GeneBasics
        }
        `
          : ""
      }
      ${
        queryParams.withTranscriptLocations
          ? `
        transcriptLocations {
            ...TranscriptLocation
        }
        `
          : ""
      }
      ${
        queryParams.withProteins
          ? `
        proteins {
          ...ProteinBasics
        }
        `
          : ""
      }
      ${
        queryParams.withLiteratureReferences
          ? `
        literatureReferences {
          ...ExternalReference
        }
        `
          : ""
      }
      ${
        queryParams.withOtherReferences
          ? `
        externalReferences {
          ...ExternalReference
        }
        `
          : ""
      }
      ${
        queryParams.withInternalReferences
          ? `
        internalReferences {
          ...InternalReference
        }
        `
          : ""
      }
    }
    ${Fragments.transcriptBasics}
    ${queryParams.withGenes ? Fragments.geneBasics : ""}
    ${queryParams.withProteins ? Fragments.proteinBasics : ""}
    ${queryParams.withTranscriptLocations ? Fragments.transcriptLocation : ""}
    ${queryParams.withLiteratureReferences || queryParams.withOtherReferences ? Fragments.externalReference : ""}
    ${queryParams.withInternalReferences ? Fragments.internalReference : ""}
  `;
};

GridFragments.transcriptLocationGrid = (queryParams: IDataTableQueryParams) => {
  return gql`
    fragment TranscriptLocationGrid on TranscriptLocation {
      id
      groupType
      transcript {
        ...TranscriptGrid
      }
      region {
        contig {
          id
          name
          accession
          sequenceId
          length
        }
        range {
          start
          stop
        }
        strand
      }
    }
    ${GridFragments.transcriptGrid(queryParams)}
  `;
};

Fragments.pfamBasics = gql`
  fragment PfamBasics on Pfam {
    name
    description
    externalReference {
      id
      link
    }
  }
`;

GridFragments.proteinGrid = (queryParams: IDataTableQueryParams) => {
  return gql`
    fragment ProteinGrid on Protein {
      ...ProteinBasics
      version
      isoelectricPoint
      molecularWeight
      ecNumbers {
        name
      }
      creator {
        username
      }
      ${
        queryParams.withGenes
          ? `
        genes {
          ...GeneBasics
        }`
          : ""
      }
      ${
        queryParams.withProteinLocations
          ? `
        proteinLocations {
            ...ProteinLocation
        }
        `
          : ""
      }
      ${
        queryParams.withTranscripts
          ? `
        transcripts {
          ...TranscriptBasics
        }
        `
          : ""
      }
      ${
        queryParams.withLiteratureReferences
          ? `
        literatureReferences {
          ...ExternalReference
        }
        `
          : ""
      }
      ${
        queryParams.withOtherReferences
          ? `
        externalReferences {
          ...ExternalReference
        }
        `
          : ""
      }
      ${
        queryParams.withPFam
          ? `
        pfamMotifs {
          ...PfamBasics
        }
        `
          : ""
      }
      ${
        queryParams.withInternalReferences
          ? `
        internalReferences {
          ...InternalReference
        }
        `
          : ""
      }
      ${
        queryParams.withPathways
          ? `

          pathways {
            ...PathwayNames
          }
        `
          : ""
      }
      ${
        queryParams.withBlastResults
          ? `

          bestBlastResults {
            ...BlastResult
          }
        `
          : ""
      }
    }
    ${Fragments.proteinBasics}
    ${queryParams.withGenes ? Fragments.geneBasics : ""}
    ${queryParams.withTranscripts ? Fragments.transcriptBasics : ""}
    ${queryParams.withProteinLocations ? Fragments.proteinLocation : ""}
    ${queryParams.withLiteratureReferences || queryParams.withOtherReferences ? Fragments.externalReference : ""}
    ${queryParams.withPFam ? Fragments.pfamBasics : ""}
    ${queryParams.withInternalReferences ? Fragments.internalReference : ""}
    ${queryParams.withPathways ? Fragments.pathwayNames : ""}
    ${queryParams.withBlastResults ? Fragments.blastResult : ""}
  `;
};

GridFragments.orthologGrid = (queryParams: IDataTableQueryParams) => {
  return gql`
    fragment OrthologGrid on Ortholog {
      id
      groupType
      protein {
        ...ProteinGrid
      }
      clusterSet {
        name
        description
      }
    }
    ${GridFragments.proteinGrid(queryParams)}
  `;
};

GridFragments.directionalOrthologGrid = (queryParams: IDataTableQueryParams) => {
  return gql`
    fragment DirectionalOrthologGrid on DirectionalOrtholog {
      id
      groupType
      protein {
        ...ProteinGrid
      }
    }
    ${GridFragments.proteinGrid(queryParams)}
  `;
};

GridFragments.regionFlagGrid = (_queryParams: IDataTableQueryParams) => {
  return gql`
    fragment RegionFlagGrid on RegionFlag {
      id
      groupType
      name
      description
      project {
        id
        name
        color
      }
      region {
        contig {
          ...ContigBasics
        }
        range {
          start
          stop
        }
      }
      modified
      lastModifier {
        id
        username
      }
      created
      creator {
        id
        username
      }
    }
    ${Fragments.contigBasics}
  `;
};

// we include the ProteinGrid, the only thing that we do not need is the protein.proteinLocations
// but we do not include it via queryParams.withProteinLocations
GridFragments.proteinLocationGrid = (queryParams: IDataTableQueryParams) => {
  return gql`
    fragment ProteinLocationGrid on ProteinLocation {
      id
      groupType
      protein {
        ...ProteinGrid
      }
      region {
        ...RegionBasics
      }
    }
    ${GridFragments.proteinGrid(queryParams)}
    ${Fragments.regionBasics}
  `;
};

// used in /admin/contentPermissions/users
GridFragments.userGrid = (_queryParams: IDataTableQueryParams) => {
  return gql`
    fragment UserGrid on User {
      ...UserBasics
      lastUse
      userGroups {
        id
        name
      }
    }
    ${Fragments.userBasics}
  `;
};

Fragments.sampleTypeBasics = gql`
  fragment SampleTypeBasics on SampleType {
    id
    name
    description
    creator {
      ...UserBasics
    }
    created
    statusSet {
      id
      name
      description
    }
  }
  ${Fragments.userBasics}
`;

GridFragments.sampleTypeGrid = (_queryParams: IDataTableQueryParams) => {
  return gql`
    fragment SampleTypeGrid on SampleType {
      ...SampleTypeBasics
      allowManualStatusChange
      userGroups {
        id
        name
      }
    }
    ${Fragments.sampleTypeBasics}
  `;
};

GridFragments.sampleGrid = (queryParams: IDataTableQueryParams) => {
  return gql`
    fragment SampleGrid on Sample {
      id
      groupType
      name
      description
      locked
      status {
        id
        name
        color
      }
      reports {
        ...ProjectFileTile
      }
      project {
        id
        name
        color
      }
      type {
        id
        name
      }
      created
      modified
      submitter {
        ...UserBasics
      }
      receiver {
        ...UserBasics
      }
      organism {
        ...OrganismGrid
      }
    }
    ${Fragments.userBasics}
    ${Fragments.projectFileTile}
    ${GridFragments.organismGrid(queryParams)}
  `;
};

GridFragments.genomeAssemblyGrid = (_queryParams: IDataTableQueryParams) => {
  return gql`
    fragment GenomeAssemblyGrid on GenomeAssembly {
      id
      assemblyAccession
      organismName
      assemblyName
      intraspecificName
      isolate
      releaseDate
      status
      submitter
      taxid
      url
      contigCount
      scaffoldCount
      moleculeCount
      totalLength
    }
  `;
};

GridFragments.markerGrid = () => {
  return gql`
    fragment MarkerGrid on Marker {
      id
      description
      owner
      created
      modified
      synonyms
      markerStatus {
        ...MarkerStatus
      }
      externalReferences {
        ...ExternalReference
      }
      name
      species {
        ...Species
      }
      regions {
        ...RegionBasics
      }
      sequenceContent
    }
    ${Fragments.species}
    ${Fragments.markerStatus}
    ${Fragments.regionBasics}
    ${Fragments.externalReference}
  `;
};

GridFragments.markerNusGrid = (queryParams: IDataTableQueryParams) => {
  return gql`
    fragment MarkerNusGrid on MarkerNus {
      id
      groupType
      marker {
        ...MarkerGrid
      }
      region {
        ...RegionBasics
      }
    }
    ${GridFragments.markerGrid(queryParams)}
    ${Fragments.regionBasics}
  `;
};

Fragments.blastHspBasics = gql`
  fragment BlastHspBasics on BlastHsp {
    id
    alignmentLength
    queryCoverage
    eValue
    ident
    pos
    gaps
    pValue
    rawScore
    score
    frame
  }
`;

Fragments.blastHsp = gql`
  fragment BlastHsp on BlastHsp {
    id
    rank
    alignmentLength
    queryStart
    queryStop
    targetStart
    targetStop
    queryCoverage
    eValue
    ident
    pos
    gaps
    pValue
    rawScore
    score
    querySequence
    targetSequence
    queryStrand
    targetStrand
    midline
    frame
  }
`;

Fragments.organismsInfo = gql`
  fragment OrganismsInfo on OrganismsInfo {
    all
    derived
    primary
    reference
    sequenced
  }
`;

Fragments.listingMapEntry = gql`
  fragment ListingMapEntry on ListingMapEntry {
    columnField
    key
    label
    value
    tableFilter
  }
`;

Fragments.organismsByVariantAnalysisGroup = gql`
  fragment OrganismsByVariantAnalysisGroup on OrganismsByVariantAnalysisGroup {
    info {
      ...OrganismsInfo
    }
    listing {
      ...ListingMapEntry
    }
  }
  ${Fragments.organismsInfo}
  ${Fragments.listingMapEntry}
`;

GridFragments.organismGrid = (queryParams: IDataTableQueryParams) => {
  return gql`
    fragment OrganismGrid on Organism {
      ...OrganismBasics
      sequencingType
      isReference
      strain
      created
      sequencingType
      taxonomy
      genotypeDescription
      version
      customId
      species {
        ...Species
      }
      variantAnalysisGroups {
        id
        name
      }
      origins
      organisation {
        id
        name
      }
      userGroups {
        id
        name
      }
      sample @include(if: ${queryParams.withSamples || false}) {
        ...SampleTile
        reports {
          ...ProjectFileTile
        }
        receiver {
          display
        }
        submitter {
          display
        }
      }
      ${
        queryParams.withGenomeStatistics
          ? `
        genomeStatistics {
          genomeSize
          contigCount
          geneCount
          transcriptsCount
          proteinsCount
          ecCoverage
          orfsCount
          flagCount
        }
        `
          : ""
      }
      ${
        queryParams.withLiteratureReferences
          ? `

        literatureReferences {
          ...ExternalReference
        }
        `
          : ""
      }
      ${
        queryParams.withOtherReferences
          ? `
        externalReferences {
          ...ExternalReference
        }
        `
          : ""
      }
      components {
        id
        name
      }

      virtualGenomes {
        id
        name
      }
    }
    ${Fragments.organismBasics}
    ${Fragments.sampleTile}
    ${Fragments.species}
    ${Fragments.projectFileTile}
    ${queryParams.withLiteratureReferences || queryParams.withOtherReferences ? Fragments.externalReference : ""}
  `;
};

Fragments.variantProteinMutation = gql`
  fragment VariantProteinMutation on ProteinMutation {
    variant {
      mutId
    }
    name
    proteinLocation {
      protein {
        id
        accession
        sequenceId
        description
        systematicName
        alternativeName
      }
    }
    effect
    range {
      start
      stop
    }
  }
`;

GridFragments.variantGrid = (queryParams: IDataTableQueryParams) => {
  return gql`
    fragment VariantGrid on Variant {
      id
      mutId
      allele
      alleleLength
      groupType
      name
      preferredName
      description
      variantType
      refAllele
      refAlleleLength
      altAllele
      altAlleleLength
      isReference
      isPresent
      matchesReference
      effect
      locationClass
      coverage
      ${
        queryParams.withHigherNeighborGenes
          ? `
        higherGenes {
          id
          name
        }
        `
          : ""
      }
      ${
        queryParams.withLowerNeighborGenes
          ? `
        lowerGenes {
          id
          name
        }
        `
          : ""
      }
      ${
        queryParams.withHigherNeighborVariants
          ? `
        higherVariants {
          id
          mutId
          name
        }
        `
          : ""
      }
      ${
        queryParams.withLowerNeighborVariants
          ? `
        lowerVariants {
          id
          mutId
          name
        }
        `
          : ""
      }
      quality
      zygosity
      profile {
        genomeId
        symbol
      }
      genome {
        id
        name
        userGroups {
          ...UserGroupBasics
        }
      }
      refGenome {
        id
        name
      }
      region {
        ...RegionBasics
      }
      ${
        queryParams.withRefRegion
          ? `
        refRegion {
          ...RegionBasics
        }
        `
          : ""
      }
      ${
        queryParams.withAnalysisGroup
          ? `
        variantAnalysisGroup {
          id
          name
        }
        `
          : ""
      }
      ${
        queryParams.withGeneMutations
          ? `
        geneMutations {
          gene {
            id
            name
          }
          effect
          locationClass
        }
        `
          : ""
      }
      ${
        queryParams.withProteinMutations
          ? `
        proteinMutations {
          ...VariantProteinMutation
        }
        `
          : ""
      }
      ${
        queryParams.withRefVariant
          ? `
        refVariant {
          id
          name
          genome {
            id
            name
          }
        }
        `
          : ""
      }
    }
    ${queryParams.withProteinMutations ? Fragments.variantProteinMutation : ""}
    ${Fragments.regionBasics}
    ${Fragments.userGroupBasics}
  `;
};

GridFragments.vcfVariantGrid = (_queryParams: IDataTableQueryParams) => {
  return gql`
    fragment VcfVariantGrid on VCFVariant {
      id
      allele
      alleleLength
      name
      variantType
      refAllele
      altAllele
      region {
        ...RegionBasics
      }
      profileVariant {
        genomeId
        genomeName
        present
        symbol
        heterozygous
        noCall
      }
      refGenome {
        id
        name
      }
      fractionInformativeReads
      varianceConfidenceQuality
      interpolatedGeneticPosition
      annAaPosByLength
      annAllele
      annAnnotation
      annAnnotationImpact
      annCDNAPosByLength
      annCdsPosByLength
      annDistance
      annErrorsWarningsInfo
      annFeatureId
      annFeatureType
      annGeneId
      annGeneName
      annHgvsC
      annHgvsP
      annRank
      annTranscriptBioType
      lofGeneId
      lofGeneName
      lofNunTranscriptsInGene
      lofPercTranscriptsAffected
      nmdGeneId
      nmdGeneName
      nmdNumTranscriptsInGene
      nmdPercTranscriptsAffected

      vcfVariantInstances {
        qiSampleCountWithCoverage
        qiGenotypeTotals
        qiCallTotals
        qiGenotype
        qiAllelicDepthsOrderedByGt
        qiReadDepth
        qiGenotypeQuality
        qiVariantStatus
        qiCallStrength
        varGenome {
          id
          name
        }
        zygosity
        alleleFraction
        indelCount
        snpPhredLikelihood
      }
    }
    ${Fragments.regionBasics}
  `;
};

// Beware: the *Grid name must match the Entity Type upperFirstChar(name) + Grid!
GridFragments.mutationGrid = (queryParams: IDataTableQueryParams) => {
  return gql`
    fragment MutationGrid on GenMutation {
      id
      groupType
      refVariant {
        ...VariantGrid
      }
      ${
        queryParams.withMutVariants
          ? `
        mutVariants {
          ${
            queryParams.withMutProteinMutations
              ? `
          proteinMutations {
            ...VariantProteinMutation
          }`
              : ""
          }

          genome {
            id
            name
          }
          region {
            ...RegionBasics
          }
          locationClass
          effect
          quality
          coverage
          zygosity
          description
        }
        `
          : ""
      }
    }
    ${queryParams.withMutVariants && queryParams.withMutProteinMutations ? Fragments.variantProteinMutation : ""}
    ${queryParams.withMutVariants ? Fragments.regionBasics : ""}
    ${GridFragments.variantGrid(queryParams)}
  `;
};

GridFragments.geneMutationGrid = (queryParams: IDataTableQueryParams) => {
  return gql`
    fragment GeneMutationGrid on GeneMutation {
      id
      groupType
      effect
      locationClass
      gene {
        ...GeneGrid
      }
      variant {
        ...VariantGrid
      }
    }
    ${GridFragments.geneGrid(queryParams)}
    ${GridFragments.variantGrid(queryParams)}
  `;
};

GridFragments.proteinMutationGrid = (queryParams: IDataTableQueryParams) => {
  // the below grid accounts for two different flavors (variant [Protein => Variants] or protein [Variant => Affected Proteins] perspective)
  // in the case of Protein perspective we import the ProteinLcoationGrid but this one fetches too much information therefore we do use our
  // own version of protein.genes and protein.transcripts subfragments (relying on queryParams withGenes = NO and withTranscripts = NO to prevent the
  // ProteinLocationGrid fragment to include these.)
  return gql`
    fragment ProteinMutationGrid on ProteinMutation {
      id
      groupType
      effect
      name
      range {
        start
        stop
      }

      ${
        queryParams.tableQuerySpec.queryType === ENTITY_TYPE.MUTATION /* protein perspective ! */
          ? `
        proteinLocation {
          ...ProteinLocationGrid
          protein {
            genes {
              id
              name
              synonyms
              geneType
            }
            transcripts {
              id
              accession
              sequenceId
            }
          }
        }
        variant {
          id
          ${
            queryParams.withGeneMutations
              ? `
          geneMutations {
            effect
            locationClass
          }
          `
              : ""
          }
        }
        `
          : ""
      }

      ${
        queryParams.tableQuerySpec.queryType === ENTITY_TYPE.PROTEIN /* variant perspective ! */
          ? `
        variant {
          ...VariantGrid
        }
        `
          : ""
      }
    }
    ${queryParams.tableQuerySpec.queryType === ENTITY_TYPE.PROTEIN ? GridFragments.variantGrid(queryParams) : ""}
    ${
      queryParams.tableQuerySpec.queryType === ENTITY_TYPE.MUTATION
        ? GridFragments.proteinLocationGrid(queryParams)
        : ""
    }
  `;
};

GridFragments.aaFeatureGrid = (_queryParams: IDataTableQueryParams) => {
  return gql`
    fragment AaFeatureGrid on AaFeature {
      id
      name
      version
      description
      range {
        start
        stop
      }
      region {
        ...RegionBasics
      }
      protein {
        id
        accession
        sequenceId
      }
      genome {
        id
        name
      }
      score
      pValue
    }
    ${Fragments.regionBasics}
  `;
};

GridFragments.nuFeatureGrid = (_queryParams: IDataTableQueryParams) => {
  return gql`
    fragment NuFeatureGrid on NuFeature {
      id
      name
      description
      region {
        ...RegionBasics
      }
      gene {
        id
        name
      }
      genome {
        id
        name
      }
      score
      pValue
      externalReferences {
        ...ExternalReference
      }
    }
    ${Fragments.regionBasics}
    ${Fragments.externalReference}
  `;
};

Fragments.contigSearch = gql`
  fragment ContigSearch on ContigSearch {
    limit
    searchTerm
    contigs {
      id
      name
      accession
      sequenceId
      sequenceType
      length
      organism {
        id
        name
      }
    }
  }
`;

Fragments.datasetColumn = gql`
  fragment DatasetColumn on DatasetColumn {
    name
    field
    annotations {
      key
      value
      multiValueIndex
    }
    minValue
    maxValue
  }
`;

Fragments.columnAnnotation = gql`
  fragment ColumnAnnotation on ColumnAnnotation {
    name
    field
    type
    valueSet
    values {
      ...StringMapEntry
    }
  }
  ${Fragments.stringMapEntry}
`;

Fragments.rowAnnotation = gql`
  fragment RowAnnotation on RowAnnotation {
    name
    field
    type
  }
`;

Fragments.dataset = gql`
  fragment Dataset on Dataset {
    id
    name
    description
    isGenomeDefault
    isGlobalDefault
    genomes {
      ...OrganismBasics
    }
    columns {
      ...DatasetColumn
    }
    columnAnnotations {
      ...ColumnAnnotation
    }
    rowAnnotations {
      ...RowAnnotation
    }
  }
  ${Fragments.organismBasics}
  ${Fragments.datasetColumn}
  ${Fragments.columnAnnotation}
  ${Fragments.rowAnnotation}
`;

Fragments.listedDataset = gql`
  fragment ListedDataset on Dataset {
    id
    name
    description
    isGenomeDefault
    isGlobalDefault
    project {
      id
      name
      color
    }
    fileMetadata {
      path
      humanReadablePath
      organisms {
        id
        name
      }
    }
  }
`;

Fragments.tableQuerySpec = gql`
  fragment TableQuerySpec on TableQuerySpec {
    id
    idType
    queryType
    resultType
    resultSubTypeId
    restrictions {
      refGenomeId
      variantGenomeIds
    }
  }
`;

Fragments.tableView = gql`
  fragment TableViewState on TableViewState {
    rowIds
    selectedIds
    totalRowCount
    filteredRowCount
    expansionPath
    tableQuerySpec {
      ...TableQuerySpec
    }
    columns {
      ...GridColumn
    }
  }
  ${Fragments.tableQuerySpec}
`;

Fragments.view = gql`
  fragment View on View {
    ...ViewBasics
    itemTags {
      id
      name
      color
    }
    state {
      genomeView {
        genome {
          ...GenomeViewGenome
        }
        contig {
          ...GenomeViewerContig
        }
        range {
          ...Range
        }
        tracks {
          ...Track
        }
        hideFeatureTable
        verticalLayout
        showFlags
        showCommunityAnnotation
        useSpecificFlagsColoring
        condensedFlagsDisplay
        headerWidth
        variantExpressionFilters
        orderedMutantGenomes {
          id
          name
        }
        featureTableRestriction
        tableView {
          ...TableViewState
        }
        refGenome {
          id
          name
          isReference
        }
      }
      tableView {
        ...TableViewState
      }
      blastView {
        colorMismatchesOnly
      }
      datasetView {
        dataset {
          ...Dataset
        }
        selectedAnnotations {
          label
          conditionValues
        }
        dataSeries {
          ...DataSeries
        }
        selectedDataSeries {
          name
          color
          hidden
          splitByCondition
          splitParent
          splitValue
          splitMultiValueIndex
        }
        chartLayout {
          chartType
          isLogScale
          timeSeries
          errorBars
          filterOutEmptyValues
          symbolSize
        }
        showFilterPanel
        filteredOutAnnotations {
          name
          categoricalValues
          minNumericalValue
          maxNumericalValue
        }
        groupMode
        hideEmptyDataSeries
        chartSelection
        chartTableSelection
        splitPaneLayout {
          verticalLayout
          hidden
          size
        }
        groupTableView {
          ...TableViewState
        }
      }
      contentBrowserView {
        groupByAttribute
        selectedEntry {
          columnField
          key
          label
          value
          tableFilter
        }
        tableView {
          ...TableViewState
        }
      }
      summaryView {
        refGenomeId
      }
    }
  }
  ${Fragments.tableView}
  ${Fragments.gridColumn}
  ${Fragments.viewBasics}
  ${Fragments.genomeForGenomeBrowser}
  ${Fragments.genomeViewerContig}
  ${Fragments.dataset}
  ${Fragments.range}
  ${Fragments.track}
  ${Fragments.dataSeries}
  ${Fragments.variantAnalysisGroup}
`;

Fragments.jobBasics = gql`
  fragment JobBasics on BlastJob {
    id
    created
    name
    status
    event {
      ...EventSummary
    }
    firstResultId
    resultCount
    tool
    owner {
      ...UserBasics
    }
  }
  ${Fragments.userBasics}
  ${Fragments.eventSummary}
`;

Fragments.blastIndex = gql`
  fragment BlastIndex on BlastIndex {
    id
    name
    secondaryName
    indexName
    type
    virtual
    sequenceType
    url
    dbSize
  }
`;

Fragments.blastHitBasics = gql`
  fragment BlastHitBasics on BlastHit {
    id
    rank
    maxScore
    eValue
    hspCount
    totalScore
    target {
      id
      name
      accession
      sequenceId
      description
      length
      type {
        name
        label
      }
      blastIndex {
        ...BlastIndex
      }
      organism {
        ...OrganismBasics
      }
      virtualGenomes {
        ...OrganismBasics
      }
      species {
        ...Species
      }
      database {
        ...DatabaseBasics
      }
      version
      isExternal
    }
  }
  ${Fragments.organismBasics}
  ${Fragments.species}
  ${Fragments.blastIndex}
  ${Fragments.databaseBasics}
`;

Fragments.blastHitBestHsp = gql`
  fragment BlastHitBestHsp on BlastHit {
    ...BlastHitBasics
    bestHsp {
      ...BlastHspBasics
    }
  }
  ${Fragments.blastHspBasics}
  ${Fragments.blastHitBasics}
`;

Fragments.blastHit = gql`
  fragment BlastHit on BlastHit {
    ...BlastHitBasics
    hsps {
      ...BlastHsp
    }
  }
  ${Fragments.blastHsp}
  ${Fragments.blastHitBasics}
`;

Fragments.jobResultBasics = gql`
  fragment JobResultBasics on JobResult {
    id
    jobId
    query {
      ...Sequence
    }
    blastHits(bestHit: $bestHit) {
      ...BlastHitBestHsp
    }
  }
  ${Fragments.blastHitBestHsp}
  ${Fragments.sequence}
`;

Fragments.jobResult = gql`
  fragment JobResult on JobResult {
    id
    jobId
    query {
      ...Sequence
    }
    blastHits(bestHit: $bestHit) {
      ...BlastHit
    }
  }
  ${Fragments.sequence}
  ${Fragments.blastHit}
`;

Fragments.jobParams = gql`
  fragment JobParams on JobParams {
    blast {
      blastType
      sequenceQuery
      files
      targets {
        ...BlastIndex
      }
      advancedOptions {
        compositionalAdjustments
        expectThreshold
        filterLowComplexity
        gapCost
        maskLower
        maskTable
        matchMismatchScores
        matrix
        maxMatches
        maxTargetSeqs
        shortQueries
        wordSize
        genCode
      }
    }
  }
  ${Fragments.blastIndex}
`;

Fragments.jobForRerun = gql`
  fragment JobForRerun on BlastJob {
    ...JobBasics
    params {
      ...JobParams
    }
  }
  ${Fragments.jobParams}
  ${Fragments.jobBasics}
`;

Fragments.job = gql`
  fragment Job on BlastJob {
    ...JobBasics
    eventName
    eventDescription
    errorDescription
    results {
      ...JobResultBasics
    }
    notification {
      id
      read
    }
    params {
      ...JobParams
    }
  }
  ${Fragments.jobResultBasics}
  ${Fragments.jobParams}
  ${Fragments.jobBasics}
`;

GridFragments.blastHitGrid = (queryParams: IDataTableQueryParams) => {
  return gql`
    fragment BlastHitGrid on BlastHit {
      ...BlastHitBasics
      queryId
      jobId
      groupType
      bestHsp {
        ...BlastHspBasics
        rank
        queryStart
        queryStop
        targetStart
        targetStop
        relatedGenes{
          id
          name
          strand
          ${queryParams.withSequence ? "sequence" : ""}
        }
        ${queryParams.withSequence ? "targetSequence" : ""}
      }
      protein {
        ...ProteinBasics
        gc
        molecularWeight
        ecNumbers {
          id
          name
        }
      }
      target {
        id
        ${queryParams.withSequence ? "sequence" : ""}
      }
    }
    ${Fragments.blastHitBasics}
    ${Fragments.blastHspBasics}
    ${Fragments.proteinBasics}
  `;
};

GridFragments.blastHspGrid = (queryParams: IDataTableQueryParams) => {
  return gql`
    fragment BlastHspGrid on BlastHsp {
      ...BlastHspBasics
      rank
      sequenceId
      blastHit {
        id
        rank
        target {
          isExternal
          name
          blastIndex {
            url
          }
        }
        jobId
        queryId
      }
      jobId
      queryId
      queryStart
      queryStop
      targetStart
      targetStop
      ${queryParams.withSequence ? "targetSequence" : ""}
      relatedGenes{
        id
        name
        strand
        ${queryParams.withSequence ? "sequence" : ""}
      }
    }
    ${Fragments.blastHspBasics}
  `;
};

Fragments.groupNotification = gql`
  fragment GroupNotification on GroupNotification {
    id
    producer
    description
    group {
      id
      itemCount
    }
  }
`;

Fragments.notification = gql`
  fragment Notification on Notification {
    id
    type
    created
    read
    status
    activity {
      ...ProjectActivity
    }
    playbookEvent {
      id
      alias
      created
      status
      owner {
        ...UserBasics
      }
      job {
        ...JobBasics
        errorDescription
      }
      playbook {
        id
        name
        type {
          id
          name
        }
      }
      inputs {
        resources {
          organism {
            id
          }
        }
      }
      results {
        file {
          id
          fileName
        }
      }
    }
    workflowEvent {
      workflowEventId
      alias
    }
    workflowNotification {
      ...WorkflowNotification
    }
    groupNotification {
      ...GroupNotification
    }
  }
  ${Fragments.projectActivity}
  ${Fragments.jobBasics}
  ${Fragments.workflowNotification}
  ${Fragments.groupNotification}
  ${Fragments.userBasics}
`;

Fragments.advancedSearchTypes = gql`
  fragment AdvancedSearchTypes on AdvancedSearchConfig {
    searchTypes {
      entityType
      label
      subTypeId
      subTypeLabel
    }
  }
`;

Fragments.advancedSearchAttribute = gql`
  fragment AdvancedSearchAttribute on SearchAttribute {
    ...SearchAttribute
    sampleAttribute {
      id
      sampleTypes {
        id
        name
      }
    }
  }
  ${Fragments.searchAttribute}
`;

Fragments.activitySavedItem = gql`
  fragment ActivitySavedItem on SavedItem {
    id
    type
    name
    description
    typeDisplayName
  }
`;

Fragments.variadicSavedItem = gql`
  fragment VariadicSavedItem on SavedItem {
    ... on View {
      ...ViewBasics
    }
    ... on Group {
      ...GroupBasics
    }
    ... on Parent {
      ...ParentSavedItem
    }
    ... on RegionFlag {
      ...RegionFlag
    }
    ... on CoordinateList {
      ...CoordinateList
    }
    ... on AnalystSession {
      ...AnalystSession
    }
  }
  ${Fragments.viewBasics}
  ${Fragments.groupBasics}
  ${Fragments.parentSavedItem}
  ${Fragments.regionFlag}
  ${Fragments.coordinateList}
  ${Fragments.analystSession}
`;

Fragments.variadicCardSavedItem = gql`
  fragment VariadicCardSavedItem on SavedItem {
    id
    type
    typeDisplayName
    name
    shared
    description
    created
    creator {
      ...UserBasics
    }
    project {
      id
      name
      color
    }
    ... on View {
      viewType
      viewLabel
      params
    }
    ... on Group {
      itemCount
    }
    ... on Parent {
      sampleCount
      status {
        name
      }
    }
    ... on AnalystSession {
      modified
    }
    ... on RegionFlag {
      modified
      lastModifier {
        ...UserBasics
      }
      deleted
      region {
        contig {
          organism {
            name
            species {
              name
            }
          }
        }
      }
    }
    ... on CoordinateList {
      ...CoordinateList
    }
  }
  ${Fragments.userBasics}
  ${Fragments.coordinateList}
`;

Fragments.gridAttributeValue = gql`
  fragment GridAttributeValue on AttributeValue {
    id
    parent {
      id
      status {
        name
        color
      }
    }
    applicable
    valueIndex
    color
  }
`;

Fragments.gridFileCustomColumn = gql`
  fragment GridFileCustomColumn on BEDCustomColumn {
    stringValue
  }
`;

Fragments.sampleAttribute = gql`
  fragment SampleAttribute on Attribute {
    id
    attributeName
    attributeType {
      id
      name
    }
    fileType {
      id
      name
    }
    mandatory
    automatic
    modifiable
    editable
    valueUniqueConstraint
    decimals
    disabled
    minValue
    maxValue
    maxLength
    pattern
    patternHint
    formulaText
    coloringRule
    frozen
    groupName
    usage
    representedColumn
    editOnce
    hasData
    sqlStatement
    idAccessor
    multipleValues
    isHead
    vocabularyTerms {
      id
      name
      sortOrder
    }
    columnSet {
      id
      name
    }
    parentType {
      id
      name
    }
  }
`;

Fragments.sampleAttributeWithDynamicTerms = gql`
  fragment SampleAttributeWithDynamicTerms on Attribute {
    ...SampleAttribute
    dynamicTerms {
      id
      name
    }
  }
  ${Fragments.sampleAttribute}
`;

GridFragments.attributeGrid = (_queryParams: IDataTableQueryParams) => {
  return gql`
    fragment AttributeGrid on Attribute {
      ...SampleAttribute
      sampleTypes {
        id
        name
      }
    }
    ${Fragments.sampleAttribute}
  `;
};

GridFragments.ssaSessionGrid = (_queryParams: IDataTableQueryParams) => {
  return gql`
    fragment SsaSessionGrid on AnalystSession {
      id
      name
      description
      creator {
        username
      }
      created
      modified
      project {
        id
        name
        color
        userGroup {
          id
          name
        }
      }
    }
  `;
};

GridFragments.playbookEventGrid = (_queryParams: IDataTableQueryParams) => {
  return gql`
    fragment PlaybookEventGrid on PlaybookEvent {
      id
      created
      owner {
        display
        firstName
        lastName
        username
      }
      note
      alias
      playbook {
        id
        name
        nameSuffix
        standard
        categories {
          name
        }
      }
      status
      results {
        resourceType
      }
      job {
        id
        resultCount
      }
      project {
        id
        name
        color
        userGroup {
          id
          name
        }
      }
    }
  `;
};

GridFragments.sswSessionGrid = (_queryParams: IDataTableQueryParams) => {
  return gql`
    fragment SswSessionGrid on WorkflowSession {
      id
      name
      description
      owner {
        username
        firstName
        lastName
        display
      }
      created
      modified
      project {
        id
        name
        color
      }
    }
  `;
};

GridFragments.bedGrid = (_queryParams: IDataTableQueryParams) => {
  return gql`
    fragment BedGrid on BEDFeature {
      id
      name
      score
      region {
        ...RegionBasics
      }
    }
    ${Fragments.regionBasics}
  `;
};

// Standalone version of parent, incl attributes values
//  but without parents
Fragments.parent = gql`
  fragment Parent on Parent {
    ...ParentBasics
    attributeValues {
      ...AttributeValue
    }
  }
  ${Fragments.parentBasics}
  ${Fragments.attributeValue}
`;

Fragments.sampleSummary = gql`
  fragment SampleSummary on Sample {
    id
    groupType
    name
    description
    locked
    created
    modified
    submitter {
      ...UserBasics
    }
    receiver {
      ...UserBasics
    }
    status {
      color
      name
    }
    history {
      id
      event {
        id
        user {
          ...UserBasics
        }
        created
        message
      }
      type
      dataComponent {
        id
        name
      }
      changes {
        id
        groupName
        attribute
        newValue
      }
    }
    reports {
      ...ProjectFileTile
    }
    type {
      id
      name
      parentTypeSets {
        isPrimary
        isRecyclable
        parentType {
          id
        }
      }
      sampleAttributes(unfiltered: true) {
        ...SampleAttribute
      }
    }
    primaryParent {
      ...ParentBasics
    }
    parents {
      ...ParentBasics
      sampleCount
      attributeValues {
        ...AttributeValue
      }
    }
    relatedProjects {
      ...RelatedProject
    }
    project {
      id
      name
      color
    }
    organism {
      ...GenomeInfoCard
      organismProperties {
        ...OrganismProperty
      }
      components {
        ...OrganismTile
      }

      virtualGenomes {
        ...OrganismTile
        sequencingType
      }
    }
  }
  ${Fragments.userBasics}
  ${Fragments.relatedProject}
  ${Fragments.attributeValue}
  ${Fragments.parentBasics}
  ${Fragments.sampleAttribute}

  ${Fragments.projectFileTile}
  ${Fragments.genomeInfoCard}
  ${Fragments.organismTile}
  ${Fragments.organismProperties}
`;

Fragments.sampleValues = gql`
  fragment SampleValues on Sample {
    allApplicableValues {
      ...AttributeValue
    }
  }
  ${Fragments.attributeValue}
`;

Fragments.sampleParentSummary = gql`
  fragment SampleParentSummary on Parent {
    ...ParentBasics
    sampleCount
    attributeValues {
      ...AttributeValue
    }
    parentType {
      parentAttributes {
        ...SampleAttributeWithDynamicTerms
      }
      sampleTypes {
        id
      }
    }
  }
  ${Fragments.parentBasics}
  ${Fragments.attributeValue}
  ${Fragments.sampleAttributeWithDynamicTerms}
`;

Fragments.sampleType = gql`
  fragment SampleType on SampleType {
    ...SampleTypeBasics
    allowManualStatusChange
    attributeCount
    sampleCount
    deletedSampleCount
  }
  ${Fragments.sampleTypeBasics}
`;

Fragments.rawAttributeDefault = gql`
  fragment RawAttributeDefault on RawAttributeDefaultMapEntry {
    attributeId
    data {
      template
      defaultNumber
      defaultText
      defaultTerm {
        id
        name
      }
    }
  }
`;

Fragments.parentTypeForSampleTypeEditing = gql`
  fragment ParentTypeForSampleTypeEditing on ParentType {
    id
    name
    description
    sampleTypes {
      id
    }
    attributeDefaults {
      ...RawAttributeDefault
    }
    parentAttributes {
      id
      attributeName
      groupName
      automatic
      modifiable
      mandatory
      maxLength
      minValue
      maxValue
      attributeType {
        id
        name
      }
      columnSet {
        id
      }
      representedColumn
      usage
      vocabularyTerms {
        id
        name
        sortOrder
      }
    }
  }
  ${Fragments.rawAttributeDefault}
`;

Fragments.sampleTypeForEditing = gql`
  fragment SampleTypeForEditing on SampleType {
    ...SampleType
    sampleAttributes(unfiltered: true) {
      ...SampleAttribute
      referencedBy {
        id
        attributeName
      }
      sampleTypes {
        id
        name
      }
    }
    parentTypeSets {
      id
      isRecyclable
      isPrimary
      parentType {
        ...ParentTypeForSampleTypeEditing
      }
    }
    sampleStatuses {
      id
      name
      statusClass {
        id
        name
      }
    }
  }
  ${Fragments.sampleType}
  ${Fragments.sampleAttribute}
  ${Fragments.parentTypeForSampleTypeEditing}
`;

Fragments.parentType = gql`
  fragment ParentType on ParentType {
    id
    name
    description
    creator {
      ...UserBasics
    }
    created
    attributeCount
    sampleCount
    sampleTypeCount
  }
  ${Fragments.userBasics}
`;

Fragments.parentTypeForEditing = gql`
  fragment ParentTypeForEditing on ParentType {
    ...ParentType
    isListed
    parentAttributes {
      ...SampleAttribute
      referencedBy {
        id
        attributeName
      }
    }
    sampleTypes {
      id
      name
    }
  }
  ${Fragments.parentType}
  ${Fragments.sampleAttribute}
`;

// ?markerGrid
Fragments.markerSummary = gql`
  fragment MarkerSummary on Marker {
    id
    name
    description
    markerType {
      id
      name
      description
    }
    created
    groupType
    modified
    owner
    sequenceContent
    synonyms
    markerStatus {
      ...MarkerStatus
    }
    externalReferences {
      ...ExternalReference
    }
    species {
      ...Species
    }
    regions {
      ...RegionBasics
    }
    relatedProjects {
      ...RelatedProject
    }
  }
  ${Fragments.species}
  ${Fragments.markerStatus}
  ${Fragments.regionBasics}
  ${Fragments.externalReference}
  ${Fragments.relatedProject}
`;

Fragments.nfConfigFragment = gql`
  fragment NFPlaybookConfig on NFPlaybookConfig {
    workflowName
    activities {
      name
      hpcEnabled
      cloudEnabled
    }
    profiles
    trackerParameters {
      sampleAttributes {
        attributeId
        correctedMandatory
        workflowParam
      }
    }
    workflowParameters {
      identifier
      label
      description
      mandatory
      hidden
      type {
        type
        subType
      }
      values {
        organism {
          ...OrganismTile
        }
        file {
          ...ProjectFileTile
        }
        textValue
        numberValue
        booleanValue
        region {
          ...RegionBasics
        }
      }
    }
  }
  ${Fragments.projectFileTile}
  ${Fragments.organismTile}
  ${Fragments.regionBasics}
`;

Fragments.processorWorkflowConfig = gql`
  fragment ProcessorWorkflowConfig on WorkflowConfig {
    sampleType {
      id
    }
    connectors {
      source
      target
    }
    activities {
      identifier
      name
      version
      disabled

      parameters {
        ...WorkflowParameter
      }
    }
  }
  ${WorkflowParameterFragmentDoc}
`;

Fragments.processorExtendedWorkflowConfig = gql`
  fragment ProcessorExtendedWorkflowConfig on WorkflowExtendedConfig {
    sampleType {
      id
    }
    connectors {
      source
      target
    }
    activities {
      identifier
      name
      label
      description
      version
      disabled

      parameters {
        ...WorkflowExtendedParameter
      }
    }
  }
  ${WorkflowExtendedParameterFragmentDoc}
`;

Fragments.processorPlaybookConfig = gql`
  fragment ProcessorPlaybookConfig on P2PlaybookConfig {
    workflow {
      id
      name
      config {
        ...ProcessorWorkflowConfig
      }
    }
  }
  ${Fragments.processorWorkflowConfig}
`;

Fragments.processorExtendedPlaybookConfig = gql`
  fragment ProcessorExtendedPlaybookConfig on P2PlaybookConfig {
    workflow {
      id
      name
      extendedConfig {
        ...ProcessorExtendedWorkflowConfig
      }
    }
  }
  ${Fragments.processorExtendedWorkflowConfig}
`;

GridFragments.coordinateListGrid = (_queryParams: IDataTableQueryParams) => {
  return gql`
    fragment CoordinateListGrid on CoordinateList {
      id
      name
      description
      project {
        ...ProjectBasics
      }
      organism {
        ...OrganismBasics
      }
      count
      creator {
        ...UserBasics
      }
      created
    }
    ${Fragments.projectBasics}
    ${Fragments.organismBasics}
    ${Fragments.userBasics}
  `;
};

GridFragments.coordinateGrid = (_queryParams: IDataTableQueryParams) => {
  return gql`
    fragment CoordinateGrid on Coordinate {
      id
      name
      score
      region {
        ...RegionBasics
      }
      nuSequence {
        ...ContigBasics
      }
    }
    ${Fragments.regionBasics}
    ${Fragments.contigBasics}
  `;
};

// dynamic fields, see queries.getDataTableQueryOptions
DynamicFragments[COLUMN_DYNAMIC_TYPES.TRACKER_ATTRIBUTE] = "...GridAttributeValue";
DynamicFragments[COLUMN_DYNAMIC_TYPES.CUSTOM_FILE_COLUMN] = "...GridFileCustomColumn";
DynamicFragments[COLUMN_DYNAMIC_TYPES.CLASSIFICATION] = "...ClassificationBasics";
DynamicFragments[COLUMN_DYNAMIC_TYPES.EXTERNAL_REFERENCE] = "...ExternalReference";
DynamicFragments[COLUMN_DYNAMIC_TYPES.ORGANISM_PROPERTY] = "...GridOrganismProperty";
